import React, { useState } from 'react';
import {
  Box,
  Typography,
  Divider,
  Avatar,
  Stack,
  Rating,
  Button,
} from '@mui/material';
import AddNewVersion from './AddNewVersion';
import AppVersionBox from './AppVersionBox';
import TestAppModal from './TestAppModal';
import { SPACING } from '../../../theme/theme';
import { useImageLightbox } from '../../Feedback/ImageLightbox';

interface AppDetailsProps {
  iconSrc: string;
  appName: string;
  appVersion: string;
  lastUpdateDate: string;
  lastUpdateTime: string;
  appSize: string;
  compatibility: string;
  ageRestriction: string;
  price: string;
  rate_count: number;
  download_count_text?: string;
  versions?: Array<{
    id: string;
    version: string;
    created_at_text: string;
  }>;
  publisherId?: string;
  appId?: string;
  active?: boolean;
  /** True once sibche has approved and published the listing. */
  canManagePublish?: boolean;
}

const AppDetailsRight: React.FC<AppDetailsProps> = ({
  iconSrc,
  appName,
  appVersion,
  lastUpdateDate,
  lastUpdateTime,
  appSize,
  versions = [],
  publisherId,
  appId,
  active = false,
  canManagePublish = false,
  compatibility,
  ageRestriction,
  price,
  rate_count,
  download_count_text,
}) => {
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  // Declared above the early return below so hook order stays stable.
  const lightbox = useImageLightbox();

  // بررسی اینکه publisherId و appId مقدار معتبر دارند
  if (!publisherId || !appId) {
    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          backgroundColor: 'white',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 10,
          padding: '16px',
          borderRadius: 1,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
          direction: 'rtl',
          fontFamily: 'IRANYekanXFaNum',
        }}
      >
        <Typography color="error">
          خطا: اطلاعات ناشر یا اپلیکیشن یافت نشد
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        backgroundColor: 'white',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 10,
        padding: '16px',
        borderRadius: 1,
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        direction: 'rtl',
        fontFamily: 'IRANYekanXFaNum',
        fontWeight: 600,
        fontSize: '14px',
        lineHeight: '20px',
        wordWrap: 'break-word',
        color: '#25282E',
        '@media (max-width: 768px)': {
          padding: '8px',
          gap: 4,
          fontSize: '12px',
        },
      }}
    >
      <Stack
        direction="row-reverse"
        alignItems="center"
        spacing={1}
        justifyContent="space-between"
        sx={{
          alignSelf: 'stretch',
          width: '100%',
        }}
      >
        <Avatar
          src={iconSrc}
          {...(iconSrc ? lightbox.triggerProps(iconSrc, 'آیکون اپلیکیشن') : {})}
          sx={{
            width: 48,
            height: 48,
            background: 'linear-gradient(0deg, #D9D9DD 0%, #D9D9DD 100%)',
            borderRadius: 1,
            cursor: iconSrc ? 'zoom-in' : undefined,
          }}
        />
        <Typography variant="body2" color="#60646E">
          آیکون اپلیکیشن
        </Typography>
      </Stack>
      <Divider />
      <Stack
        direction="row-reverse"
        justifyContent="space-between"
        alignItems="center"
        sx={{ alignSelf: 'stretch' }}
      >
        <Typography variant="subtitle1" fontWeight={700}>
          {appName}
        </Typography>
        <Typography variant="body2" color="#60646E">
          نام اپلیکیشن
        </Typography>
      </Stack>
      <Stack
        direction="row-reverse"
        justifyContent="space-between"
        alignItems="center"
        sx={{ alignSelf: 'stretch' }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {appVersion}
        </Typography>
        <Typography variant="body2" color="#60646E">
          نسخه فعال
        </Typography>
      </Stack>
      <Stack
        direction="row-reverse"
        justifyContent="space-between"
        alignItems="center"
        sx={{ alignSelf: 'stretch' }}
      >
        <Typography variant="body2">
          {lastUpdateDate} | {lastUpdateTime}
        </Typography>
        <Typography variant="body2" color="#60646E">
          آخرین آپدیت
        </Typography>
      </Stack>
      <Stack
        direction="row-reverse"
        justifyContent="space-between"
        alignItems="center"
        sx={{ alignSelf: 'stretch' }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {appSize}
        </Typography>
        <Typography variant="body2" color="#60646E">
          حجم برنامه
        </Typography>
      </Stack>
      <Stack
        direction="row-reverse"
        justifyContent="space-between"
        alignItems="center"
        sx={{ alignSelf: 'stretch' }}
      >
        <Typography variant="body1" dir="ltr">
          <Rating name="star-rating" value={rate_count} size="small" />
        </Typography>
        <Typography variant="body2" color="#60646E">
          امتیاز اپلیکیشن
        </Typography>
      </Stack>
      <Stack
        direction="row-reverse"
        justifyContent="space-between"
        alignItems="center"
        sx={{ alignSelf: 'stretch' }}
      >
        <Typography variant="subtitle2" fontWeight={600}>
          {download_count_text || '0'}
        </Typography>
        <Typography variant="body2" color="#60646E">
          تعداد دانلود
        </Typography>
      </Stack>
      <Divider sx={{ width: '100%' }} />

      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ width: '100%', marginTop: 2, marginBottom: 2 }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 500,
            fontFamily: 'IRANYekanXFaNum',
            fontSize: 12,
            wordWrap: 'break-word',
            color: '#A6AAB5',
            paddingLeft: '20px',
          }}
        >
          تست اپلیکیشن پیش از انتشار
        </Typography>
        <Typography
          sx={{
            fontWeight: 500,
            fontFamily: 'IRANYekanXFaNum',
            fontSize: 14,
            wordWrap: 'break-word',
            paddingLeft: 'px',
            paddingRight: '50px',
            color: '#A6AAB5',
          }}
        >
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsTestModalOpen(true)}
            sx={{
              width: '93px',
              height: '25px',
              borderRadius: 0.5,
              boxShadow: 0,
              border: 1,
              backgroundColor: '#F2F2F5',
              borderColor: '#A6AAB5',
              fontFamily: 'IRANYekanXFaNum',
              fontSize: 12,
              lineHeight: '20px',
              wordWrap: 'break-word',
              padding: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#60646E',
              textTransform: 'none',
              // Matches the version-row buttons in AppVersionBox: the fill
              // darkens with the text so white stays readable.
              transition: 'background-color .2s ease, color .2s ease',
              '&:hover': {
                backgroundColor: '#60646E',
                borderColor: '#60646E',
                color: '#FFFFFF',
              },
            }}
          >
            توضیحات
          </Button>
        </Typography>
      </Stack>

      <Stack
        alignItems="center"
        spacing={4}
        justifyContent="space-between"
        sx={{
          alignSelf: 'stretch',
          width: '100%',
        }}
      >
        <AddNewVersion publisherId={publisherId} applicationId={appId} />
      </Stack>
      <Box
        sx={{
          maxHeight: '200px',
          overflowY: 'auto',
          width: '100%',
          // overflow makes this a clipping box, so the rows need padding on
          // every side: with none on the right the buttons' borders and hover
          // effects were sliced off flush against the edge.
          padding: `${SPACING.xs}px ${SPACING.sm}px`,
          boxSizing: 'border-box',
          // Scrollbar styling now comes from the global rule in index.css.
        }}
      >
        <Stack
          alignItems="center"
          spacing={4}
          sx={{
            width: '100%',
          }}
        >
          {versions.length > 0 &&
            versions.map((version: any) => (
              <AppVersionBox
                key={version.id}
                version={version}
                releaseDate={version.created_at_text}
                publisherId={publisherId}
                appId={appId}
                active={active}
                canManagePublish={canManagePublish}
              />
            ))}
        </Stack>
      </Box>

      <TestAppModal
        open={isTestModalOpen}
        onClose={() => setIsTestModalOpen(false)}
        appId={appId}
      />
      {lightbox.element}
    </Box>
  );
};

export default AppDetailsRight;
